import Service from "../service";
import extendAlmaGlobal from "../../utils/almaglobal";
import { PageType } from "../dataLayer/datalayer";
import { getLocalStorageString, loadScript } from "../../utils";
import { filterRelevance } from "../meta-api";
import { getLocation } from "../../shared/geo/geo-client";
import { brandsafetyfyValue, cleanEmptyValues, encodeSalesforceValue, stringifyValue, } from "./dmp-format";
import executeInterchange from "./interchange";
import almaSessionCount from "./session-count";
import installCommandQueue from "../../utils/command-queue";
import shorten from "../../utils/string-tools";
import getBrowserSegments from "../ads/ad-targeting/segments/dmp-segments";
import requestArticleMetadata from "../meta-api/article-meta";
const defaultRelevancyLimits = {
    iabTier1: {
        min: 0,
        max: 100,
    },
    iabTier2: {
        min: 10,
        max: 100,
    },
    semantics: {
        min: 10,
        max: 100,
    },
};
class AlmaDmpService extends Service {
    constructor(config, context) {
        super("AlmaDmp", context);
        this.controlTagReady = false;
        this.consentListenerSet = false;
        this.configuration = {
            ...config,
            defaults: {
                ...config.defaults,
                relevancy: {
                    relevancyLimits: config.defaults?.relevancy?.relevancyLimits ??
                        defaultRelevancyLimits,
                },
                collectOnPageLoad: config.defaults?.collectOnPageLoad,
            },
        };
    }
    async init() {
        this.logger.logInfo(`DMP loaded`);
        this.logger.logInfo(`Execute interchange`);
        executeInterchange();
        this.logger.logInfo(`Add Alma session count`);
        almaSessionCount();
        this.addGlobals();
        if (this.configuration.defaults.collectOnPageLoad === true) {
            this.logger.logInfo(`Pageview requested with page load`);
            this.collectData();
        }
        this.commandQueue?.executeQueue(this.commandQueue.queue);
    }
    addGlobals() {
        this.logger.logInfo(`Extending ALMA global with DMP`);
        const globalInterface = {
            user: () => {
                return {
                    user: this.retrieve("user"),
                    kuid: this.retrieve("kuid"),
                    alma_id: this.context.services.dataLayerService?.dataLayer.alma_id,
                    amid: this.context.services.dataLayerService?.dataLayer.amid,
                };
            },
            segments: () => {
                return this.getSegments();
            },
            queue: window.ALMA.dmp.queue ?? [],
            trackPageView: () => {
                this.logger.logInfo(`Pageview requested from global`);
                this.collectData();
            },
            kuidToQualifio: (element) => {
                window.ALMA.cmpV2?.getPublisherConsents((consent) => {
                    if ("error" in consent) {
                        this.logger.logError(`Error getting consent for Qualifio: ${consent.error}`);
                        return;
                    }
                    if ("standard" in consent) {
                        if (consent.standard[1] &&
                            consent.standard[3] &&
                            consent.standard[5] &&
                            consent.standard[9]) {
                            this.logger.logInfo(`Consent valid for Qualifio, send message`);
                            if ("_qual_async" in window &&
                                // eslint-disable-next-line no-underscore-dangle
                                Array.isArray(window._qual_async)) {
                                const kuid = {
                                    user: this.retrieve("user"),
                                    kuid: this.retrieve("kuid"),
                                    alma_id: {
                                        alma_id: this.context.services.dataLayerService?.dataLayer
                                            .alma_id,
                                        amid: this.context.services.dataLayerService?.dataLayer
                                            .amid,
                                    },
                                };
                                // eslint-disable-next-line no-underscore-dangle
                                window._qual_async.push([
                                    "sendChildMessage",
                                    `qualifio_insert_place_${element}`,
                                    "kuid",
                                    JSON.stringify(kuid),
                                ]);
                            }
                            else {
                                this.logger.logWarn(`Could not find _qual_async from window`);
                            }
                        }
                    }
                });
            },
            info: {},
        };
        this.commandQueue = installCommandQueue("DMP-queue", globalInterface.queue);
        extendAlmaGlobal({ ...window?.ALMA?.dmp, ...globalInterface }, "dmp");
        this.updateInfoObject();
    }
    collectData() {
        const collect = async () => {
            this.logger.logInfo(`Data collection requested`);
            try {
                let customProps;
                this.logger.logInfo("Checking for preDataCollection hook");
                if (this.configuration.preDataCollection) {
                    this.logger.logInfo("Found preDataCollection, awaiting it");
                    const result = await this.configuration.preDataCollection(this.context);
                    if (typeof result === "object" && "reason" in result) {
                        this.logger.logInfo(`DMP data collection cancelled: ${result.reason}`);
                        return;
                    }
                    this.logger.logInfo(`preDataCollection returned ${JSON.stringify(result, null, 2)}`);
                    customProps = result.props;
                }
                const snapshot = await this.createDataLayerSnapshot(customProps);
                if (!snapshot) {
                    this.logger.logError(`Cancelling data collection - could not form DMP data`);
                    return;
                }
                extendAlmaGlobal({ ...window?.ALMA?.dmp, data: snapshot }, "dmp");
                if (this.controlTagReady === false) {
                    this.logger.logInfo(`Requesting consent for DMP`);
                    const consent = await this.context.services.almaCmpService?.getPublisherConsents();
                    if (consent) {
                        await this.injectControlTag(this.configuration.siteId);
                        await this.setDmpConsent(consent);
                    }
                }
                else {
                    extendAlmaGlobal({ ...window?.ALMA?.dmp, data: snapshot }, "dmp");
                    this.makeAjaxRequest();
                }
                this.updateInfoObject();
            }
            catch (e) {
                this.logger.logError(`DMP failed due to: ${e}`);
            }
        };
        const collectionTimeout = this.configuration.defaults.waitBeforeCollect ?? 2000;
        if (this.collectionRequestTimeout) {
            this.logger.logInfo(`Clearing stale page view request`);
            window.clearTimeout(this.collectionRequestTimeout);
        }
        this.logger.logInfo(`Pageview requested, executing in ${collectionTimeout} ms`);
        this.collectionRequestTimeout = window.setTimeout(collect, collectionTimeout);
        // if ("requestIdleCallback" in window) {
        //   this.logger.logInfo(`Collecting DMP data once browser is idle`);
        //   window.requestIdleCallback(collect, { timeout: 1000 });
        // } else {
        //   this.logger.logInfo(`Collecting DMP data after timeout`);
        //   window.setTimeout(collect, 1000);
        // }
    }
    getSegments() {
        return (this.retrieve("segs") ?? "").split(",");
    }
    updateInfoObject() {
        this.logger.logInfo(`Update DMP info object`);
        const browserSegments = getBrowserSegments();
        extendAlmaGlobal({
            ...window.ALMA.dmp,
            info: {
                KVX: Object.entries(browserSegments).reduce((acc, [k, v]) => {
                    if (v.length === 1) {
                        return { ...acc, [k]: Number.parseInt(v[0], 10) };
                    }
                    return acc;
                }, {}),
                segments: this.getSegments(),
            },
        }, "dmp");
        // Broadcast that we have updated
        // GTM - Maybe we can assume GTM has initialized before something that is loaded through it?
        if (window.dataLayer && typeof window.dataLayer.push === "function") {
            window.dataLayer.push({
                event: "Alma DMP",
                "DMP status": "DMP info ready",
            });
        }
        // Generic JS event like from Alma-DMP
        if (window.dispatchEvent) {
            const infoUpdatedEvent = new CustomEvent("alma_dmp_info_ready");
            window.dispatchEvent(infoUpdatedEvent);
        }
    }
    async createDataLayerSnapshot(customProps) {
        this.logger.logInfo(`Create data layer snapshot`);
        if (!this.context.services.dataLayerService) {
            throw new Error("Data layer service not available");
        }
        const { dataLayer } = this.context.services.dataLayerService;
        const geoLoc = await getLocation(1500, true);
        const defaultUserProps = [
            {
                name: "alma_id",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.alma_id;
                },
            },
            {
                name: "amid",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.amid;
                },
            },
            {
                name: "email_sha256",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.cdime;
                },
            },
            {
                name: "locationApproximationCity",
                resolve: async () => {
                    return geoLoc?.approximation?.country !== "abroad"
                        ? geoLoc?.approximation?.city
                        : undefined;
                },
            },
            {
                name: "locationApproximationRegion",
                resolve: async () => {
                    return geoLoc?.approximation?.country !== "abroad"
                        ? geoLoc?.approximation?.region
                        : undefined;
                },
            },
            {
                name: "site_user_id",
                resolve: async (context) => {
                    return context.services.dataLayerService?.dataLayer.cdimu;
                },
            },
        ];
        const isBrandSafe = await this.context.services.brandsafetyService?.isSafe("dmp");
        let filteredMetaData = {};
        if (dataLayer.pageType === PageType.Article &&
            typeof dataLayer.pageId === "string") {
            const limits = this.configuration.defaults.relevancy?.relevancyLimits ??
                defaultRelevancyLimits;
            this.logger.logInfo(`Filtering DMP meta: ${JSON.stringify(limits, null, 2)}`);
            const metaData = await requestArticleMetadata(this.context);
            filteredMetaData = {
                iabTier1: filterRelevance(metaData?.iabTier1 ?? [], limits.iabTier1.min, limits.iabTier1.max),
                iabTier2: filterRelevance(metaData?.iabTier2 ?? [], limits.iabTier2.min, limits.iabTier2.max),
                leikiHighDef: filterRelevance(metaData?.leikiHighDef ?? [], limits.semantics.min, limits.semantics.max),
            };
            filteredMetaData.locationJournalisticCity = metaData?.location?.[0]?.city;
            filteredMetaData.locationJournalisticRegion =
                metaData?.location?.[0]?.region;
        }
        const defaultPageProps = [
            {
                name: "OPUid",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.amid;
                },
            },
            {
                name: "alma_id_",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.alma_id;
                },
            },
            {
                name: "bs",
                resolve: async () => {
                    if (typeof isBrandSafe !== "undefined") {
                        return isBrandSafe ? "1" : "0";
                    }
                    return undefined;
                },
            },
            {
                name: "cdime",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.cdime;
                },
            },
            {
                name: "cdimu",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.cdimu;
                },
            },
            {
                name: "iabTier1",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier1;
                },
            },
            {
                name: "iabTier2",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.iabTier2;
                },
            },
            {
                name: "leikiHighDef",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.leikiHighDef;
                },
            },
            {
                name: "locationJournalisticCity",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.locationJournalisticCity;
                },
            },
            {
                name: "locationJournalisticRegion",
                brandSafety: true,
                resolve: () => {
                    return filteredMetaData.locationJournalisticRegion;
                },
            },
            {
                name: "primaryCategory",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.primaryCategory;
                },
            },
            {
                name: "sc",
                resolve: () => {
                    return window.ALMA.dmp.sc()?.toString(10) ?? "0";
                },
            },
            {
                name: "section",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.primaryCategory;
                },
            },
            {
                name: "shareURL",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.locationHref;
                },
            },
            {
                name: "subsection",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.subCategory1;
                },
            },
            {
                name: "subCategory1",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.subCategory1;
                },
            },
            {
                name: "subCategory2",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer
                        .subCategory2;
                },
            },
            {
                name: "subCategory3",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer
                        .subCategory3;
                },
            },
            {
                name: "tags",
                resolve: (context) => {
                    return context.services.dataLayerService?.dataLayer.pageTags;
                },
            },
        ];
        try {
            const pageData = [
                ...defaultPageProps,
                ...(this.configuration.customProps?.page ?? []),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(this.configuration.resolveCustomProps?.(this.context).page ??
                    []),
                ...(customProps?.page ?? []),
            ];
            const userProps = [
                ...defaultUserProps,
                ...(this.configuration.customProps?.user ?? []),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(this.configuration.resolveCustomProps?.(this.context).user ??
                    []),
                ...(customProps?.user ?? []),
            ];
            const cleanData = await this.resolveDataLayer(userProps, pageData, isBrandSafe);
            Object.entries(cleanData.page).forEach(([key, value]) => {
                // eslint-disable-next-line no-return-assign
                return (cleanData.page[key] = shorten(value, 254, ","));
            });
            this.logger.logInfo(`Data layer snapshot: ${JSON.stringify(cleanData, null, 2)}`);
            return cleanData;
        }
        catch (e) {
            this.logger.logInfo(`DMP Data layer snapshot failed: ${e}`);
        }
        return undefined;
    }
    async resolveDataLayer(userProperties, pageProperties, isBrandSafe) {
        this.logger.logInfo(`Resolving DMP data layer. ${userProperties.length} user props, ${pageProperties.length} page props`);
        const resolveProps = async (props) => {
            const values = (await Promise.all(props.map(async (prop) => {
                try {
                    let result = await prop.resolve(this.context);
                    if (!Array.isArray(result)) {
                        result = [result];
                    }
                    result = result.map(stringifyValue);
                    if (prop.brandSafety && isBrandSafe === false) {
                        result = result.map(brandsafetyfyValue);
                    }
                    result = result.map(encodeSalesforceValue);
                    return {
                        key: prop.name,
                        value: result,
                    };
                }
                catch (e) {
                    this.logger.logError(`Unable to resolve ${prop.name} due to ${e}`);
                    return { key: prop.name, value: undefined };
                }
            }))).reduce((acc, prop) => {
                return { ...acc, [prop.key]: prop.value };
            }, {});
            return cleanEmptyValues(values);
        };
        return {
            user: await resolveProps(userProperties),
            page: await resolveProps(pageProperties),
        };
    }
    async setDmpConsent(consent) {
        this.logger.logInfo(`Received consent from CMP`);
        return new Promise((resolve) => {
            if (consent) {
                const sfConsent = {
                    dc: consent.standard[1] === true,
                    al: !!(consent.standard[1] === true && consent.standard[3] === true),
                    tg: !!(consent.standard[1] === true &&
                        consent.standard[3] === true &&
                        consent.standard[4] === true),
                    cd: !!(consent.standard[1] === true &&
                        consent.standard[3] === true &&
                        consent.standard[4] === true &&
                        consent.specialFeature[2]),
                    sh: !!(consent.standard[1] === true &&
                        consent.standard[3] === true &&
                        consent.standard[4] === true &&
                        consent.custom[1] === true),
                    re: !!(consent.standard[1] === true &&
                        consent.standard[3] === true &&
                        consent.standard[4] === true &&
                        consent.specialFeature[2]), // Re-identification
                };
                this.logger.logInfo(`Sending consent ${Object.entries(sfConsent).map(([key, value]) => `${key}:${value}`)}`);
                window.Krux("ns:almamedia", "consent:set", sfConsent, () => {
                    this.logger.logInfo(`Consent sent`);
                    if (!this.consentListenerSet) {
                        const consentChangedEvent = "alma_cmp_v2_has_optedin";
                        this.logger.logInfo(`Listening for changes with ${consentChangedEvent}`);
                        window.addEventListener(consentChangedEvent, (event) => {
                            this.logger.logInfo(`Consent changed, update`);
                            this.setDmpConsent(event.detail);
                        }, false);
                        this.consentListenerSet = true;
                    }
                    resolve();
                });
            }
            else {
                this.logger.logError(`DMP consent was to be updated, but received no consent to send`);
            }
        });
    }
    async injectControlTag(siteId) {
        this.logger.logInfo(`Inject SF Control Tag script`);
        const scriptE = document.createElement("script");
        scriptE.setAttribute("class", "kxct");
        scriptE.setAttribute("data-id", siteId);
        scriptE.setAttribute("data-timing", "async");
        scriptE.setAttribute("data-version", "3.0");
        scriptE.type = "text/javascript";
        window.document.head.appendChild(scriptE);
        await loadScript(`https://cdn.krxd.net/controltag/${siteId}.js`, window);
        this.controlTagReady = true;
        this.logger.logInfo(`Control tag ready`);
    }
    async makeAjaxRequest() {
        return new Promise((resolve, reject) => {
            this.logger.logInfo(`Firing SF Ajax request`);
            window.Krux("ns:almamedia", "page:load", (err) => {
                if (err) {
                    this.logger.logError(`DMP failed - Ajax request error: ${err}`);
                    reject();
                }
                else {
                    resolve();
                }
            }, { pageView: true });
        });
    }
    retrieve(parameter) {
        const kruxDataStorageKey = `kxalmamedia_${parameter}`;
        this.logger.logInfo(`Retrieve key ${kruxDataStorageKey} from Krux data storage`);
        let value = null;
        const lsItem = getLocalStorageString(kruxDataStorageKey);
        if (lsItem) {
            value = lsItem;
        }
        else if (navigator.cookieEnabled) {
            const browserCookies = document.cookie.match(`${kruxDataStorageKey}=([^;]*)`);
            value = (browserCookies && unescape(browserCookies[1])) ?? null;
        }
        this.logger.logInfo(`Retrieved value ${value} for key ${kruxDataStorageKey}`);
        return value;
    }
}
export default AlmaDmpService;
//# sourceMappingURL=almadmp.js.map